// ===========================================
// Achievement Grid Component
// ===========================================
// Grid display of multiple achievement badges

import { Box, Typography, Skeleton } from '@mui/material';
import { Achievement, AchievementDefinition } from '../../types/gamification.types';
import AchievementBadge from './AchievementBadge';

interface AchievementGridProps {
  achievements: Achievement[];
  definitions?: AchievementDefinition[];
  showUnearned?: boolean;
  size?: 'small' | 'medium' | 'large';
  maxDisplay?: number;
  emptyMessage?: string;
  isLoading?: boolean;
  onAchievementClick?: (achievement: Achievement | AchievementDefinition) => void;
}

export default function AchievementGrid({
  achievements,
  definitions,
  showUnearned = false,
  size = 'medium',
  maxDisplay,
  emptyMessage = 'No achievements yet',
  isLoading = false,
  onAchievementClick,
}: AchievementGridProps) {
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        {[1, 2, 3, 4].map((i) => (
          <Skeleton
            key={i}
            variant="circular"
            width={size === 'small' ? 48 : size === 'medium' ? 64 : 96}
            height={size === 'small' ? 48 : size === 'medium' ? 64 : 96}
          />
        ))}
      </Box>
    );
  }

  // Get earned achievement definition IDs
  const earnedDefIds = new Set(achievements.map((a) => a.achievementDefinitionId).filter(Boolean));

  // Combine earned achievements with unearned definitions if needed
  const displayItems: Array<{
    data: Achievement | AchievementDefinition;
    earned: boolean;
  }> = [];

  // Add earned achievements
  achievements.forEach((achievement) => {
    displayItems.push({ data: achievement, earned: true });
  });

  // Add unearned definitions if showUnearned is true
  if (showUnearned && definitions) {
    definitions
      .filter((def) => !earnedDefIds.has(def.id))
      .forEach((def) => {
        displayItems.push({ data: def, earned: false });
      });
  }

  // Apply max display limit
  const itemsToShow = maxDisplay ? displayItems.slice(0, maxDisplay) : displayItems;
  const hasMore = maxDisplay && displayItems.length > maxDisplay;

  if (itemsToShow.length === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 4,
          color: 'text.secondary',
        }}
      >
        <Typography variant="body2">{emptyMessage}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          justifyContent: { xs: 'center', sm: 'flex-start' },
        }}
      >
        {itemsToShow.map((item) => (
          <AchievementBadge
            key={'earnedAt' in item.data ? item.data.id : `def-${item.data.id}`}
            achievement={item.data}
            size={size}
            earned={item.earned}
            onClick={onAchievementClick ? () => onAchievementClick(item.data) : undefined}
          />
        ))}
      </Box>

      {hasMore && (
        <Typography
          sx={{
            mt: 2,
            textAlign: 'center',
            color: 'text.secondary',
            fontSize: '0.875rem',
          }}
        >
          +{displayItems.length - maxDisplay} more
        </Typography>
      )}
    </Box>
  );
}
